import { TenderTimeProvider } from "../../../context/TenderTimeProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import style from "./common.module.scss";
import { Aviation, File } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import OrdersInfoCargo from "./components/OrdersInfoCargo";
import OrdersInfoHeader from "./components/OrdersInfoHeader";
import OrdersInfoBasic from "./components/OrdersInfoBasic";
import OrdersInfoDirections from "./components/OrdersInfoDirections";
import Loading from "../../shared/Loading/Loading";
import OrderHistoryMobile from "./components/mobile/OrderHistoryMobile";
import OrderInfoAviationMobileActions from "./components/mobile/OrderInfoAviationMobileActions";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";
import OrderAccessRestricted from "../../orders-new/order-info/components/OrderAccessRestricted";

const normalizeOrderDates = (sourceOrder) => {
  const order = { ...sourceOrder };

  if (order.shippingDate) {
    const [date] = order.shippingDate.split("T");
    const timeStart = String(order.shippingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.shippingHourTo ?? 0).padStart(2, "0");
    order.shippingDate = `${date}T${timeStart}:00:00`;
    order.shippingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  if (order.unloadingDate) {
    const [date] = order.unloadingDate.split("T");
    const timeStart = String(order.unloadingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.unloadingHourTo ?? 0).padStart(2, "0");
    order.unloadingDate = `${date}T${timeStart}:00:00`;
    order.unloadingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  return order;
};

const OrderInfoAviationMobile = ({ tenderOrderInfo }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const { currentLanguage } = useLanguage();
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const translationRef = useRef(t);
  translationRef.current = t;

  const [order, setOrder] = useState(null);
  const [bookingDate, setBookingDate] = useState();
  const [statusHistory, setStatusHistory] = useState([]);
  const [routeStartCity, setRouteStartCity] = useState(null);
  const [coordinate, setCoordinate] = useState([]);
  const [listDocDriver, setListDocDriver] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [suggestedPrices, setSuggestedPrices] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isOpenModalHistory, setIsOpenModalHistory] = useState(false);
  const [isOpenChatModal, setIsOpenChatModal] = useState(false);

  const fetchOrderData = useCallback(async () => {
    if (!orderId) return null;

    setIsLoading(true);
    try {
      const { data } = await Aviation.getAirOrdersById(orderId, currentLanguage);
      const nextOrder = normalizeOrderDates({
        ...data,
        expeditorName: tenderOrderInfo?.expeditorName || data?.expeditorName,
      });
      setOrder(nextOrder);
      return nextOrder;
    } catch (error) {
      console.error(error);
      toast.error(translationRef.current("orderInfo.genericError"));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentLanguage, orderId, tenderOrderInfo?.expeditorName]);

  const fetchDocuments = useCallback(
    async (currentOrderId) => {
      if (!currentOrderId) return;

      try {
        const [typeSixResponse, typeSevenResponse] = await Promise.all([
          File.getFilesByOrder(currentOrderId, 6),
          File.getFilesByOrder(currentOrderId, 7),
        ]);
        setDocuments(
          [...(typeSixResponse.data || []), ...(typeSevenResponse.data || [])].reverse(),
        );
      } catch (error) {
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      }
    },
    [],
  );

  const onForceUpdate = useCallback(() => fetchOrderData(), [fetchOrderData]);

  useEffect(() => {
    setOrder(null);
    setStatusHistory([]);
    setSuggestedPrices([]);
    setDocuments([]);
    setUnreadMessagesCount(0);
    setIsOpenModalHistory(false);
    setIsOpenChatModal(false);
    fetchOrderData();
  }, [fetchOrderData]);

  useEffect(() => {
    if (!order?.id) return undefined;

    let isActive = true;
    setIsLoadingMap(true);

    Aviation.getUnreadMessages(order.id)
      .then((response) => {
        if (isActive && response.status === 200) {
          setUnreadMessagesCount(response.data?.count || 0);
        }
      })
      .catch((error) => console.error(error));

    Aviation.getHistory(order.id)
      .then((response) => {
        if (!isActive) return;
        const history = Array.isArray(response.data) ? response.data : [];
        setStatusHistory(history);
        setBookingDate(history[3]?.createdAt);
      })
      .catch((error) => {
        if (!isActive) return;
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      });

    Aviation.getListSuggestedPriceAir(order.id)
      .then((response) => {
        if (isActive) {
          setSuggestedPrices(Array.isArray(response?.data) ? response.data : []);
        }
      })
      .catch((error) => {
        if (isActive) console.error(error);
      });

    fetchDocuments(order.id);

    return () => {
      isActive = false;
    };
  }, [fetchDocuments, order?.id]);

  const isAccessRestricted =
    user?.type === "Expeditor" &&
    order &&
    order.status !== "awaitingoffer" &&
    order.companyId !== user.companyId &&
    order.expeditorId !== user.companyId;

  return (
    <TenderTimeProvider sessionKey={JSON.stringify([orderId, user?.companyId ?? user?.company?.id])}>
    <div className={cn(style.wrapper_mobile)}>
      {order ? (
        <>
          <OrdersInfoHeader item={order} onForceUpdate={onForceUpdate} />
          {isLoading ? <Loading borderRadius="16px" /> : null}

          {isAccessRestricted ? (
            <OrderAccessRestricted isModal={false} />
          ) : (
            <div className={style.orders_content}>
              <OrdersInfoBasic
                item={order}
                onOpen={() => setIsOpenModalHistory(true)}
                onOpenChat={() => setIsOpenChatModal(true)}
                unreadCount={unreadMessagesCount}
              />

              {isOpenModalHistory ? (
                <OrderHistoryMobile
                  item={order}
                  isOpen={isOpenModalHistory}
                  statusHistory={statusHistory}
                  onClose={() => setIsOpenModalHistory(false)}
                  senderName={order.senderName}
                  recipientName={order.recipientName}
                  cityFrom=""
                  cityTo=""
                  isLoadingMap={isLoadingMap}
                  routeStartCity={routeStartCity}
                  coordinateTruck={coordinate}
                  bookingDate={bookingDate}
                  listDocDriver={listDocDriver}
                  listFilesOrder={documents}
                  refreshDocuments={() => fetchDocuments(order.id)}
                />
              ) : null}

              {isOpenChatModal ? (
                <OrdersChatHub
                  isOpen={isOpenChatModal}
                  orderData={order}
                  orderId={order.id}
                  orderNumber={order.number}
                  onClose={() => setIsOpenChatModal(false)}
                  serviceType="aviation"
                />
              ) : null}

              <OrdersInfoDirections
                item={order}
                senderName={order.senderName}
                recipientName={order.recipientName}
              />

              <OrderInfoAviationMobileActions
                order={order}
                statusHistory={statusHistory}
                suggestedPrices={suggestedPrices}
                fetchOrderData={fetchOrderData}
                onForceUpdate={onForceUpdate}
                refreshDocuments={() => fetchDocuments(order.id)}
                onClose={() => navigate(-1)}
                user={user}
              />

              <OrdersInfoCargo getOrder={order} />
              <span className={style.orders_author} />
            </div>
          )}
        </>
      ) : null}
    </div>
    </TenderTimeProvider>
  );
};

export default OrderInfoAviationMobile;
